import { useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { standardsApi } from '@edulastic/api'
import { pdl as pdlConstants } from '@edulastic/constants'
import { captureSentryException, notification } from '@edulastic/common'
import { sortPAStandards } from '../../utils/helpers'

const { pearToPdlGradeMap } = pdlConstants

const handleError = (error, context) => {
  captureSentryException(error, {
    extra: context,
  })
  notification({
    type: 'error',
    msg: error?.response?.data?.error || 'An error occurred. Please try again.',
  })
}

const fetchPAStandards = async (pdlIds) => {
  if (!pdlIds?.length) {
    return []
  }

  try {
    const response = await standardsApi.getPaStandards({ pdlIds })
    return response
  } catch (error) {
    handleError(error, { pdlIds })
    return []
  }
}

const transformStandards = (pdlStandards, paStandards) => {
  // Sort PA standards before matching
  const sortedPAStandards = sortPAStandards([...paStandards])

  return pdlStandards
    .map((pdlStandard) => {
      const matchingPAStandard = sortedPAStandards.find(
        (pa) => `${pa.pdlId}` === `${pdlStandard.id}`
      )
      if (!matchingPAStandard) {
        return
      }

      const {
        tloDescription,
        tloIdentifier,
        tloId,
        _id,
        identifier,
        description,
        curriculumId,
        pdlId,
        level,
        grades,
      } = matchingPAStandard
      return {
        _id,
        identifier,
        description,
        curriculumId,
        pdlId,
        tloDescription,
        tloIdentifier,
        tloId,
        level,
        grades,
      }
    })
    .filter(Boolean)
}

const PDLStandardsModal = ({
  pdlCurriculumId,
  pdlAdoptYear,
  pdlSubjectId,
  curriculumId,
  curriculum,
  subject,
  grades,
  pdlStandardIds,
  eloStandards,
  onApply,
  onCancel,
  isSearchFilter,
  setIsLoading,
}) => {
  const isModalOpen = useRef(false)

  const cleanupModal = () => {
    isModalOpen.current = false
  }

  const handleStandardsSelection = async (obj) => {
    try {
      // Picker resolves with an empty object when closed without a selection
      if (!obj || !Object.keys(obj).length) {
        cleanupModal()
        onCancel()
        return
      }
      const pdlIds = obj.standards.map((standard) => standard.id)
      setIsLoading(true)
      const paStandards = await fetchPAStandards(pdlIds)
      setIsLoading(false)

      if (!pdlIds.length) {
        if (isSearchFilter) {
          onApply([])
        } else {
          onApply({
            standard: { id: curriculumId, curriculum },
            eloStandards: [
              ...eloStandards.filter(
                (standard) => standard.curriculumId !== curriculumId
              ),
            ],
            grades,
            subject,
          })
        }
        cleanupModal()
        return
      }

      if (!paStandards.length) {
        cleanupModal()
        return
      }

      const transformedStandards = transformStandards(
        obj.standards,
        paStandards
      )

      if (isSearchFilter) {
        onApply([...transformedStandards])
      } else {
        onApply({
          standard: { id: curriculumId, curriculum },
          eloStandards: [
            ...eloStandards.filter(
              (standard) => standard.curriculumId !== curriculumId
            ),
            ...transformedStandards,
          ],
          grades,
          subject,
        })
      }
      cleanupModal()
    } catch (error) {
      setIsLoading(false)
      handleError(error, {
        pdlCurriculumId,
        curriculumId,
        subject,
        pdlStandardIds,
      })
      cleanupModal()
    }
  }

  const openModal = () => {
    const openStandardsModal = window?.pear?.standards?.openStandardsModal
    if (!openStandardsModal) {
      handleError(new Error('Standards modal function not available'))
      return
    }

    if (isModalOpen.current) {
      return
    }

    isModalOpen.current = true

    // Convert PA grades to PDL grade IDs
    const pdlGradeIds =
      grades && grades.length > 0
        ? grades.map((grade) => pearToPdlGradeMap(grade)).filter(Boolean)
        : undefined

    openStandardsModal({
      hideDropdownFilters: true,
      searchParams: {
        standardPublicationId: `${pdlCurriculumId || ''}`,
        subjectId: `${
          pdlSubjectId || pdlConstants.paToPdlSubjectIdMap(subject) || ''
        }`,
        gradeLevelIds: pdlGradeIds,
        ...(pdlAdoptYear ? { adoptYear: `${pdlAdoptYear}` } : {}),
      },
      standardIds: pdlStandardIds.map((id) => `${id}`),
    })
      .then(handleStandardsSelection)
      .catch((error) => {
        handleError(error, {
          pdlCurriculumId,
          curriculumId,
          subject,
          pdlStandardIds,
        })
        cleanupModal()
      })
  }

  useEffect(() => {
    openModal()
    return () => {
      cleanupModal()
    }
  }, [])

  useEffect(() => {
    if (isModalOpen.current) {
      cleanupModal()
      if (onCancel) {
        onCancel()
      }
    }
  }, [
    pdlCurriculumId,
    pdlAdoptYear,
    pdlSubjectId,
    subject,
    JSON.stringify(pdlStandardIds),
  ])

  return null
}

PDLStandardsModal.propTypes = {
  pdlCurriculumId: PropTypes.string.isRequired,
  pdlAdoptYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  pdlSubjectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  curriculumId: PropTypes.string.isRequired,
  curriculum: PropTypes.string,
  subject: PropTypes.string,
  grades: PropTypes.arrayOf(PropTypes.string),
  pdlStandardIds: PropTypes.arrayOf(PropTypes.string),
  eloStandards: PropTypes.arrayOf(PropTypes.object),
  onApply: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  setIsLoading: PropTypes.func,
}

PDLStandardsModal.defaultProps = {
  pdlAdoptYear: '',
  pdlSubjectId: '',
  subject: '',
  curriculum: '',
  grades: [],
  pdlStandardIds: [],
  eloStandards: [],
  onCancel: () => {},
  setIsLoading: () => {},
}

export default PDLStandardsModal
